import themeColors, { themeFontsSize } from "@GeriMed/styles/variables";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
        css: css`
        border-bottom: 1px solid ${themeColors.lightGray5};
        box-shadow: 0 3px 5px 0 rgba(0, 0, 0, .2);
        z-index: 9;
        .customer-info-wrap {
            background-color: ${themeColors.lightGray67};

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 15px;
                        `,
                    ],
                    "max",
                )}

            .change-customer,
            .customer-info {
                width: 100%;
                margin: 0 auto;
                max-width: 1220px;
            }
            .customer-info {
                padding-top: 15px;
            }

            .change-customer {
                display: flex;
                justify-content: end;
                padding-bottom:15px;
            }

            .inf-label {
                font-weight: 600;
                line-height: ${themeFontsSize.size24};
                font-size: ${themeFontsSize.size14};
                margin: 8px 8px 8px 0;
            }

            .inf-cust span {
                color: ${themeColors.neroColor};
                font-size: ${themeFontsSize.size14};
                font-family: "ProximaNova", Helvetica, Arial, sans-serif;
            }

            .inf-value {
                font-weight: 400;
                margin-right: 15px;
            }
            .inf-cust {
                margin: 8px 0;
            }
            .info-tbl {
                width: 100%;
                max-width: 295px;
                border-collapse: collapse;
                border: 1px solid ${themeColors.lightGray};
            }

            .info-tbl th,
            .info-tbl td {
                text-align: left;
                line-height: 1.5em;
                padding: 1em 0.75em;
                vertical-align: top;
            }

            .info-tbl th {
                font-weight: 600;
            }

            .info-tbl th,.info-tbl td {
                font-size: ${themeFontsSize.size13};
                line-height: ${themeFontsSize.size18};
                color: ${themeColors.colorRaisinBlack};
            }

            .info-tbl tr:nth-child(even) {
                background-color: ${themeColors.colorWhite};
            }

            .info-tbl tr:nth-child(odd) {
                border-bottom: 3px solid ${themeColors.colorGray};
                background-color: ${themeColors.lightGray20};
            }

            .info-tbl th .sort {
                position: relative;
                display: inline-block;
                padding-right: 0.75em;
            }

            .info-tbl th .sort:hover,
            .info-tbl th .sort:focus {
                text-decoration: none;
                color: inherit;
            }

            .inf-cust {
                margin: 8px 0;
            }
        }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`   
                    .change-customer {
                        padding: 0 36px;
                    }
                    `,
                ],
                "min",
            )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`

                        `,
                        null,
                        null,
                        css`
                            &.guest .headerSearchWrapper{
                                margin-left: 40px;
                                input:not(.react-datetime-picker__inputGroup__input),
                                .search-history-content,
                                .search-autocomplete {
                                    min-width: 900px;
                                }
                                .search-popover {
                                    overflow: hidden;
                                    margin-top: 2px;
                                }
                            } 
                        `,
                    ],
                    "min",
                )}
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .search-history-content,
                            .search-autocomplete {
                                min-width: 600px;
                            }
                        `,
                    ],
                    "min",
                )}
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                        div.search-popover {
                            width: 93%;
                        }
                        `,
                        css`
                        .search-popover {
                            top: 70px !important;
                            width: 96%;
                            left: 15px;
                        }
                        .search-history-content {
                            width: auto;
                        }
                        `,
                    ],
                    "max",
                )}

                &.logged-in {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            null,
                            css`
                                .site-logo-wrapper {
                                    margin-top: -20px;
                                }
                            `,
                            null,
                            null,
                        ], "min")}
                }
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 33%;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                &.category-wrap {
                                    width: 100%;
                                    margin: 0 auto;
                                    padding: 0 14px;
                                    max-width: 1220px;
                                    justify-content: space-between;

                                    [class*="Wrapper--"]{
                                        width: auto;
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                &.category-wrap {
                                    max-width:100%;
                                    width:100%;
                                    
                                    .cart-wrap [class*="StyledButton"]{
                                        width: 100%;
                                        justify-content: center;
                                    }
                                    > [class*="Wrapper"],.cart-wrap{
                                        flex-basis: 50%;
                                    }
                                }
                            `,
                        ],
                        "max",
                    )}
            }
            .category-wrap {
                .cart-wrap a:hover,.cart-wrap a:hover span {
                    color: #fff !important;

                    &.ln-subhead {
                        color: #bccde5 !important;
                    }
                }
            } 
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: ${themeFontsSize.size12};
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, currentUserIsGuest, dismissRfqUpdates }: Props) => {
    
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <Page as="header" className={isAuthenticated?"logged-in":"guest"} isHeader={true} {...styles.page}>
            <SkipNav
                extendedStyles={styles.skipToContent}
                text={translate("Skip to main content")}
                destination={afterHeader}
            />
            <EagerLoadingLogoProvider value={true}>
                <StyledWrapper className="additional-header">
                    <Hidden above="sm">
                        <Zone contentId={id} zoneName="HeaderSearchMobilew" />
                    </Hidden>
                    <Zone contentId={id} zoneName="HeaderBudget" />
                    <Zone contentId={id} zoneName="SecondaryNavigation" />
                    <Zone contentId={id} zoneName="SignIn" />
                </StyledWrapper>
                <StyledWrapper className="category-nav" {...styles.container}>
                    <StyledWrapper className="category-wrap">
                        <Zone contentId={id} zoneName="MainNavigation" fixed />
                        <Hidden below="md">
                            <Zone contentId={id} zoneName="Search" />
                        </Hidden>
                        <StyledWrapper className="cart-wrap">
                            <Zone contentId={id} zoneName="Cart" fixed />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <Zone contentId={id} zoneName="AdditionalMenuLink" />
                <Zone contentId={id} zoneName="Breadcrumb" />
            </EagerLoadingLogoProvider>
            <span ref={afterHeader} tabIndex={-1} />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
